/**
 * Calculator Page - Cash-out amount calculator
 *
 * Matches the design from the mobile interface with country/state selectors,
 * amount calculation, fees, and total display.
 * All data is stored in URL parameters for persistence using TanStack Router.
 */

import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { TokenUSDC } from "@web3icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { RampsButton } from "@/components/ramps/Button";
import { EditableTextWithThickCaret as EditableText } from "@/components/ramps/EditableTextWithThickCaret";
import { RampsSelectBottomSheet } from "@/components/ramps/RampsSelectBottomSheet";
import { env } from "@/config/env";
import {
	CORE_API_ERROR_CODES,
	type CoreApiErrorCode,
} from "@/constants/core-api-error-codes";
import { useApiClientWithAutoVersion } from "@/hooks/useApiClientWithAutoVersion";
import { useCalculatorExperiment } from "@/hooks/useCalculatorExperiment";
import { useContinue } from "@/hooks/useContinue";
import { useCountries } from "@/hooks/useCountries";
import { FEATURE_FLAGS, useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useAnalytics } from "@/hooks/usePostHog";
import { capitalizeFirstLetter, formatNumber } from "@/lib/utils";
import { processAddressLines } from "@/utils/addressLines";
import { normalizeSenderNames } from "@/utils/senderNames";
// Import our new API layer
import type { Country, QuoteResponse, Region } from "../api";
// Import the new Calculator component for A/B testing
import { Calculator } from "../components/ramps/Calculator";
import { CoreApiErrorPage } from "../components/ramps/CoreApiErrorPage";
import { LegalNameConfirmationDrawer } from "../components/ramps/LegalNameConfirmationDrawer";
// Import the NearbyLocationsCounter component
import { Locator } from "../components/ramps/Locator";
import { MaintenancePage } from "../components/ramps/MaintenancePage";
import { NearbyLocationsCounter } from "../components/ramps/NearbyLocationsCounter";
import { RampsText } from "../components/ramps/Text";
import { Drawer, DrawerContent } from "../components/ui/drawer";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import {
	findSubdivisionByRegionName,
	getCountriesWithSubdivisions,
} from "../constants/subdivisions";
// Import domain services
import {
	buildQuoteRequest,
	formatAmount,
	isAmountReady,
	isBankService,
	isDirectToAccountEnabled,
	isFormValid,
	minMaxError,
	type SearchParams,
	validateAmount,
} from "../domain/calculator";
import { resolvePayoutCountry } from "../lib/country-resolver";
import { getCurrentLanguage } from "../lib/i18n-loader";
import { navigateWithRampsState } from "../lib/ramps-navigation";
import {
	getApiVersionFromSearchParams,
	getAuthTokenFromSearchParams,
	validateRampsSearch,
} from "../lib/ramps-search-schema";
import {
	ANALYTICS_EVENTS,
	type CalculatorAnalyticsProperties,
} from "../utils/analytics";

// Helper function to get localized error message from quote API errors
function getLocalizedErrorMessage(error: {
	category: string;
	code: string;
	message: string;
	offendingFields: string[];
}): string {
	const currentLang = getCurrentLanguage();

	// Handle MAPI-20000/000000 - Invalid amount error
	if (error.category === "MAPI-20000" && error.code === "000000") {
		// Extract the amount from the message using regex
		const amountMatch = error.message.match(/(\d+\.\d{2})/);

		if (amountMatch && currentLang === "es") {
			const amount = amountMatch[1];
			return i18n._("Amount entered is not valid, please try {amount}", {
				amount: amount,
			});
		}
	}

	// Default: return API message
	return error.message || `Error ${error.code}: ${error.category}`;
}

import { toast } from "@/lib/toast.tsx";
import { extractKycDataFromCompliance } from "../stores/ramps-store";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";

// Types for UI components
interface CountryOption {
	value: string;
	label: string;
	countryCode: string;
}

interface RegionOption {
	value: string;
	label: string;
}

// --- ISO2/ISO3 normalization utilities ---
const ISO2_TO_3: Record<string, string> = { US: "USA", MX: "MEX", CA: "CAN" };

function toIso3(code?: string, countries?: Country[]): string {
	if (!code) return "";
	const c = code.toUpperCase();
	if (c.length === 3) return c;
	if (c.length === 2)
		return (
			ISO2_TO_3[c] || countries?.find((x) => x.iso2Code === c)?.iso3Code || ""
		);
	return "";
}

function CalculatorPage() {
	const search = Route.useSearch(); // Get all params from URL
	const store = useRampsStore();
	const navigate = useNavigate();
	const { trackPageView, trackEvent, trackTransactionAmount } = useAnalytics();

	// A/B Experiment: Calculator UI test
	const { variant: experimentVariant, getExperimentProperties } =
		useCalculatorExperiment();

	// Translation strings for Lingui extraction
	// These are used in getLocalizedErrorMessage for runtime translation
	const _errorTranslations = {
		invalidAmount: t`Amount entered is not valid, please try {amount}`,
	};
	void _errorTranslations; // Suppress unused variable warning

	// Get min/max amounts from store (set by PostHog feature flags)
	// Default minimum amount is 15
	const minAmount = useRampsStore((state) => state.minAmount || 15);
	const maxAmount = useRampsStore((state) => state.maxAmount || 10000);
	const cashInFlowEnabled = useRampsStore(
		(state) => state.cashInFlowEnabled ?? true,
	);

	// Centralized amount management with validation
	const [readyForQuote, setReadyForQuote] = useState(false);
	const [inputAmount, setInputAmount] = useState("");
	const [isAmountEditable, setIsAmountEditable] = useState(true);
	const [isPendingNewQuote, setIsPendingNewQuote] = useState(false);
	const readyForQuoteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Mode state for location selection (country or subdivision)
	const [locationSheetMode, setLocationSheetMode] = useState<
		"country" | "subdivision"
	>("country");
	// Controlled open state for location sheets
	const [showLocationSheet, setShowLocationSheet] = useState(false);
	const [showLocatorSheet, setShowLocatorSheet] = useState(false);
	// RAMPS-4410: legal-name verification bottom sheet shown to brand-new users
	// on Continue, before the flow proceeds toward sendValidate.
	const [showNameConfirmDrawer, setShowNameConfirmDrawer] = useState(false);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (readyForQuoteTimeoutRef.current) {
				clearTimeout(readyForQuoteTimeoutRef.current);
			}
		};
	}, []);

	// Detect API version from URL parameters (accesstoken vs token)
	// - accesstoken → v5 API (OAuth, context from URL params)
	// - token → v3 API (JWT, context from prepare endpoint)
	const authToken = getAuthTokenFromSearchParams(search) || store.token;
	const detectedVersionFromUrl = getApiVersionFromSearchParams(search);
	// Use detected version if explicit, otherwise fall back to store or default
	const detectedVersion = detectedVersionFromUrl ?? store.apiVersion ?? "v3";

	// CRITICAL: Set API version synchronously BEFORE creating API client
	// This ensures serviceOptions query uses the correct API version on first render
	if (authToken && store.apiVersion !== detectedVersion) {
		rampsActions.setApiVersion(detectedVersion);
		if (env.isDevelopment) {
			console.log(
				`🔍 API Version detected: ${detectedVersion} from URL parameter: ${
					search.accesstoken ? "accesstoken (OAuth)" : "token (JWT)"
				}`,
			);
		}
	}

	// Initialize API client with detected version
	// OAuth tokens → v5 API, JWT tokens → v3 API
	const { apiClient, walletAddress } = useApiClientWithAutoVersion({
		authToken,
		walletAddress: search.walletAddress,
	});

	// Get feature flag for DIRECT_TO_ACCT
	const { isFeatureEnabled, getFeatureFlagPayload } = useFeatureFlags();
	const directToAccountFlagEnabled = isFeatureEnabled(
		FEATURE_FLAGS.ENABLE_DIRECT_TO_ACCT,
	);
	const directToAccountPayload = getFeatureFlagPayload(
		FEATURE_FLAGS.ENABLE_DIRECT_TO_ACCT,
	);

	// Check if DIRECT_TO_ACCT is enabled for the current country
	const isDirectToAccountAvailable = isDirectToAccountEnabled(
		directToAccountFlagEnabled,
		directToAccountPayload?.supportedCountries,
		store.payoutCountry,
	);

	// Derive UI state from experiment variant for consistency with tracking
	// Use experimentVariant instead of separate isFeatureEnabled call to ensure
	// the UI shown matches what's recorded in analytics events
	const isNewCalculatorEnabled = experimentVariant === "test";

	// Log feature flag status in development
	useEffect(() => {
		if (env.isDevelopment && store.payoutCountry) {
			console.log("🚩 DIRECT_TO_ACCT Feature Flag:", {
				flagEnabled: directToAccountFlagEnabled,
				supportedCountries: directToAccountPayload?.supportedCountries,
				currentCountry: store.payoutCountry,
				isAvailable: isDirectToAccountAvailable,
			});
			console.log("🚩 NEW CALCULATOR A/B Experiment:", {
				experimentVariant,
				isNewCalculatorEnabled,
			});
		}
	}, [
		directToAccountFlagEnabled,
		directToAccountPayload?.supportedCountries,
		store.payoutCountry,
		isDirectToAccountAvailable,
		experimentVariant,
		isNewCalculatorEnabled,
	]);

	// Track if experiment event has been sent to avoid duplicate tracking
	const hasTrackedExperimentEvent = useRef(false);
	const hasUserSelectedSubdivision = useRef(false);

	// Sync URL search params with store on route entry and track general page view
	useEffect(() => {
		if (search.transaction_id) {
			rampsActions.setTransactionId(search.transaction_id);
		}
		// Store auth token from either parameter (token for JWT, accesstoken for OAuth)
		// TODO: After legacy repo migration, consider separate storage for JWT vs OAuth tokens
		const tokenFromUrl = search.accesstoken || search.token;
		if (tokenFromUrl) {
			rampsActions.setToken(tokenFromUrl);
		}
		if (search.walletAddress) {
			rampsActions.setWalletAddress(search.walletAddress);
		}
		// Handle amount initialization
		// v3: Use prepareAmount if present (ignore URL param)
		// v2/v5: Use URL param
		if (store.apiVersion === "v3" && store.prepareAmount) {
			const formattedAmount = formatAmount(store.prepareAmount);
			setInputAmount(formattedAmount);
			setIsAmountEditable(false); // Lock the amount for v3 prepare
		} else if (search.amount && store.apiVersion !== "v3") {
			// v2/v5: Use URL param (only if not v3)
			const formattedAmount = formatAmount(search.amount);
			setInputAmount(formattedAmount);
			setIsAmountEditable(false);
		}
		if (search.service_option) {
			rampsActions.setServiceOption(search.service_option);
		}

		// Track general page view immediately on mount (don't wait for experiment)
		// This ensures page views are never lost even if user navigates away quickly
		trackPageView("calculator", {
			page_name: "calculator",
		});
	}, [
		search.transaction_id,
		search.token,
		search.accesstoken,
		search.walletAddress,
		search.amount,
		search.service_option,
		store.prepareAmount,
		store.apiVersion,
		trackPageView,
	]);

	// Check if cash in flow is disabled and show warning toast
	useEffect(() => {
		if (!cashInFlowEnabled && store.operation === "deposit") {
			toast.warning(
				t`Cash in (deposit) flow is currently disabled. Please use the withdraw flow instead.`,
			);
		}
	}, [cashInFlowEnabled, store.operation]);

	// Track experiment-specific event once when variant is loaded
	// This is separate from page view to ensure proper experiment attribution
	useEffect(() => {
		// Only track once and only after variant is determined
		if (hasTrackedExperimentEvent.current || experimentVariant === null) {
			return;
		}

		hasTrackedExperimentEvent.current = true;

		// Track experiment-specific page view event for funnel analysis
		// This event has proper experiment attribution for PostHog analysis
		trackEvent(ANALYTICS_EVENTS.CALCULATOR_PAGE_VIEWED, {
			"$feature/ramps-new-calculator-page-enabled": experimentVariant,
			experiment_variant: experimentVariant,
			experiment_name: "calculator-ui-ab-test",
		} as Parameters<typeof trackEvent>[1]);
	}, [experimentVariant, trackEvent]);

	// Step 1: Wallet Lookup - Check for returning user profile by wallet address
	const walletLookupQuery = useQuery({
		queryKey: [
			"walletProfileLookup",
			authToken,
			walletAddress,
			store.serviceOption,
		],
		queryFn: async () => {
			if (!walletAddress) {
				throw new Error("NO_WALLET_ADDRESS");
			}

			// Check if user has a profile linked to this wallet
			const profileResponse =
				await apiClient.profile.getProfileByWalletAddress(walletAddress);

			// Validate profile response before fetching bank accounts
			if (!profileResponse || !profileResponse.digitalProfileId) {
				return {
					profile: profileResponse,
					bankAccounts: [],
				};
			}

			// Only fetch bank accounts for BANK_DEPOSIT, not DIRECT_TO_ACCT
			if (store.serviceOption !== "BANK_DEPOSIT") {
				return {
					profile: profileResponse,
					bankAccounts: [],
				};
			}

			// Fetch bank accounts for this profile
			const bankAccountsResponse =
				await apiClient.profile.getProfileBankAccounts(
					profileResponse.digitalProfileId,
				);

			return {
				profile: profileResponse,
				bankAccounts: bankAccountsResponse.bankAccounts,
			};
		},
		enabled:
			isBankService(store.serviceOption) &&
			!!authToken &&
			!store.walletLookupCompleted &&
			!store.digitalProfileId &&
			store.operation !== "deposit",
		retry: false,
		refetchOnWindowFocus: false,
	});

	// Handle wallet lookup success
	useEffect(() => {
		if (walletLookupQuery.isSuccess && walletLookupQuery.data) {
			const profile = walletLookupQuery.data.profile;
			// Check for step-up requirement and extract KYC data (only if profile exists)
			if (profile) {
				const kycData = extractKycDataFromCompliance(profile);
				if (kycData) {
					rampsActions.setKycData(
						kycData.kycToken,
						kycData.kycApplicantId,
						"api",
					);

					// Track step-up requirement in analytics
					trackEvent("step_up_required", {
						source: "wallet_lookup",
						action_provider: profile.actionProvider || "",
					} as Record<string, unknown>);
				}
			}

			// Normal flow: set profile and bank accounts (continue even if step-up required)
			if (profile?.digitalProfileId) {
				rampsActions.setDigitalProfile(profile.digitalProfileId, "wallet");
			}
			if (profile && "isStepUpRequired" in profile) {
				rampsActions.setStepUpRequired(profile.isStepUpRequired || false);
			}
			if (profile && "isAllowedToProceed" in profile) {
				rampsActions.setIsAllowedToProceed(profile.isAllowedToProceed || false);
			}

			// Only set bank account for BANK_DEPOSIT, not DIRECT_TO_ACCT
			if (
				store.serviceOption === "BANK_DEPOSIT" &&
				walletLookupQuery.data.bankAccounts.length > 0
			) {
				rampsActions.setSelectedBankAccount(
					walletLookupQuery.data.bankAccounts[0],
				);
			}
		}
	}, [
		walletLookupQuery.isSuccess,
		walletLookupQuery.data,
		trackEvent,
		store.serviceOption,
	]);

	// Handle wallet lookup error/completion
	useEffect(() => {
		if (walletLookupQuery.isError || walletLookupQuery.isSuccess) {
			if (!store.walletLookupCompleted) {
				rampsActions.setWalletLookupCompleted();
			}
		}
	}, [
		walletLookupQuery.isError,
		walletLookupQuery.isSuccess,
		store.walletLookupCompleted,
	]);

	// Step 2: Phone Lookup - If wallet lookup didn't find profile, try phone
	const phoneLookupQuery = useQuery({
		queryKey: [
			"phoneProfileLookup",
			store.phone,
			store.phoneCountryCode,
			store.serviceOption,
		],
		queryFn: async () => {
			const dialCode = store.phoneCountryCode.replace("+", "");

			// Todo: review: should there be a guard clause here is DPID was found by Wallet address?

			const preCheckResponse = await apiClient.profile.checkProfileByPhone({
				phoneNumber: store.phone,
				countryDialCode: dialCode,
			});

			if (!preCheckResponse || !preCheckResponse.digitalProfileId) {
				throw new Error("PROFILE_NOT_FOUND");
			}

			// Only fetch bank accounts for BANK_DEPOSIT, not DIRECT_TO_ACCT
			if (store.serviceOption !== "BANK_DEPOSIT") {
				return {
					preCheckResponse,
					digitalProfileId: preCheckResponse.digitalProfileId,
					bankAccounts: [],
				};
			}

			// Fetch bank accounts for this profile
			const bankAccountsResponse =
				await apiClient.profile.getProfileBankAccounts(
					preCheckResponse.digitalProfileId,
				);

			return {
				preCheckResponse,
				digitalProfileId: preCheckResponse.digitalProfileId,
				bankAccounts: bankAccountsResponse.bankAccounts,
			};
		},
		enabled:
			isBankService(store.serviceOption) &&
			store.walletLookupCompleted &&
			!store.digitalProfileId &&
			!store.phoneLookupCompleted &&
			!!store.phone &&
			!!store.phoneCountryCode &&
			store.operation !== "deposit",
		retry: false,
		refetchOnWindowFocus: false,
	});

	// Handle phone lookup success
	useEffect(() => {
		if (phoneLookupQuery.isSuccess && phoneLookupQuery.data) {
			const preCheckResponse = phoneLookupQuery.data.preCheckResponse;
			console.log("preCheckResponse", preCheckResponse); // Todo: remove:
			// Check for step-up requirement and extract KYC data
			if (preCheckResponse) {
				const kycData = extractKycDataFromCompliance(preCheckResponse);
				if (kycData) {
					rampsActions.setKycData(
						kycData.kycToken,
						kycData.kycApplicantId,
						"api",
					);

					// Track step-up requirement in analytics
					trackEvent("step_up_required", {
						source: "phone_lookup",
						action_provider: preCheckResponse.actionProvider || "",
					} as Record<string, unknown>);
				}

				if ("isStepUpRequired" in preCheckResponse) {
					rampsActions.setStepUpRequired(
						preCheckResponse.isStepUpRequired || false,
					);
				}
				if ("isAllowedToProceed" in preCheckResponse) {
					rampsActions.setIsAllowedToProceed(
						preCheckResponse.isAllowedToProceed || false,
					);
				}
			}

			// Normal flow: set profile and bank accounts (continue even if step-up required)
			rampsActions.setDigitalProfile(
				phoneLookupQuery.data.digitalProfileId,
				"phone",
			);

			// Only set bank account for BANK_DEPOSIT, not DIRECT_TO_ACCT
			if (
				store.serviceOption === "BANK_DEPOSIT" &&
				phoneLookupQuery.data.bankAccounts.length > 0
			) {
				rampsActions.setSelectedBankAccount(
					phoneLookupQuery.data.bankAccounts[0],
				);
			}
		}
	}, [
		phoneLookupQuery.isSuccess,
		phoneLookupQuery.data,
		trackEvent,
		store.serviceOption,
	]);

	// Handle phone lookup error/completion
	useEffect(() => {
		if (phoneLookupQuery.isError || phoneLookupQuery.isSuccess) {
			if (!store.phoneLookupCompleted) {
				rampsActions.setPhoneLookupCompleted(true);
			}
		}
	}, [
		phoneLookupQuery.isError,
		phoneLookupQuery.isSuccess,
		store.phoneLookupCompleted,
	]);

	// Clear selected bank account when service option changes to DIRECT_TO_ACCT
	useEffect(() => {
		if (store.serviceOption === "DIRECT_TO_ACCT" && store.selectedBankAccount) {
			rampsActions.clearSelectedBankAccount();
		}
	}, [store.serviceOption, store.selectedBankAccount]);

	// Note: Profile creation is deferred until Identification page
	// We don't create profile on Calculator page because we need ID number
	// which is only collected on the Identification page

	const handleContinue = useContinue();

	// Fetch service options
	const serviceOptionsQuery = useQuery({
		queryKey: [
			"serviceOptions",
			store.operation,
			store.assetCode,
			store.payoutCountry,
		],
		queryFn: async () => {
			const response = await apiClient.serviceOptions.getServiceOptions({
				operation: store.operation || "withdraw", // Default to withdraw to get countries
				assetCode: store.assetCode,
				userSelectedCountry: store.payoutCountry,
			});

			// Only set withdrawCountry if API provides one, we don't have a user selection yet,
			// and the country is actually supported for cash-out (RAMPS-4406)
			const normalizedWithdrawCountry = toIso3(
				response.withdrawCountry,
				allCountries,
			);
			if (
				normalizedWithdrawCountry &&
				!store.payoutCountry &&
				(response.cashOutCountries || []).includes(normalizedWithdrawCountry)
			) {
				rampsActions.setPayoutCountry(normalizedWithdrawCountry);
			}

			// Store user coordinates from serviceOptions response (fallback for when Geolocation API is denied)
			rampsActions.setUserCoordinates(
				response.latitude || "",
				response.longitude || "",
			);

			return response;
		},
		enabled: !!authToken && store.isPrepareComplete, // Wait for prepare/context before calling
		staleTime: 2 * 60 * 1000, // Cache for 2 minutes
	});

	// Sync the store payoutCurrency with serviceOptions:
	// - clear if no longer in the available list (country / service option changed)
	// - seed with the default rule when empty so the quote request reflects it
	useEffect(() => {
		if (!serviceOptionsQuery.data) return;
		const available = getAvailablePayoutCurrencies();
		if (available.length === 0) return;
		if (!store.payoutCurrency || !available.includes(store.payoutCurrency)) {
			rampsActions.setPayoutCurrency(getDefaultPayoutCurrency());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- helpers close over store/service option but reruns are driven by these inputs
	}, [serviceOptionsQuery.data, store.serviceOption, store.payoutCurrency]);

	const isCashOutMaintenance =
		isFeatureEnabled(FEATURE_FLAGS.RAMPS_CASHOUT_MAINTENANCE) ||
		serviceOptionsQuery.data?.isServerDown === true;

	// Use countries hook for centralized data access
	const {
		filteredCountries,
		isLoading: countriesLoading,
		isError: isCountriesError,
	} = useCountries({
		allowedCountryCodes: serviceOptionsQuery.data?.cashOutCountries || [],
	});

	// URL search params as single source of truth - store ISO3 codes consistently
	const allCountries = filteredCountries || [];
	const allowedIso3 = useMemo(() => {
		const list = serviceOptionsQuery.data?.cashOutCountries || [];
		return list.length ? list : allCountries.map((c) => c.iso3Code);
	}, [serviceOptionsQuery.data?.cashOutCountries, allCountries]);

	const filteredCountriesForOptions = allCountries.filter((c) =>
		allowedIso3.includes(c.iso3Code),
	);

	const notSupportedUSStates =
		serviceOptionsQuery.data?.uiConfigs?.[0]?.notSupportedUSStates ?? [];

	// // One-time country resolution on component readiness
	useEffect(() => {
		const hasServiceOptions =
			!!serviceOptionsQuery.data && !serviceOptionsQuery.isLoading;
		const hasCountries = allCountries.length > 0 && !countriesLoading;
		const isReady = hasServiceOptions && hasCountries;

		// Store suggested country from service options when available and supported (RAMPS-4406)
		const normalizedSuggestedCountry = toIso3(
			serviceOptionsQuery.data?.withdrawCountry,
			allCountries,
		);
		if (
			hasServiceOptions &&
			normalizedSuggestedCountry &&
			!store.suggestedCountry &&
			allowedIso3.includes(normalizedSuggestedCountry)
		) {
			rampsActions.setSuggestedCountry(normalizedSuggestedCountry);
		}

		// Only run once when everything is ready and we don't have resolved payout_country
		// if (!isReady || search.payout_country) return;
		if (!isReady || store.payoutCountry) {
			return;
		}

		// Validate service suggested country against allowed countries
		// If the detected/suggested country is not in cashOutCountries, default to USA
		const serviceCountry = serviceOptionsQuery.data?.withdrawCountry;
		const normalizedServiceCountry = serviceCountry
			? toIso3(serviceCountry, allCountries)
			: undefined;
		const isServiceCountryAllowed =
			normalizedServiceCountry &&
			allowedIso3.includes(normalizedServiceCountry);

		// Use service country if allowed, otherwise default to USA if it's available
		const validServiceCountry = isServiceCountryAllowed
			? normalizedServiceCountry
			: allowedIso3.includes("USA")
				? "USA"
				: undefined;

		// Resolve payout country using explicit precedence
		const resolution = resolvePayoutCountry({
			urlCountry: search.country,
			urlPayoutCountry: search.payout_country,
			urlCountrySrc: search.country_src,
			profileCountry: store.quoteResponse?.senderProfile?.address?.countryCode,
			serviceCountry: validServiceCountry,
			allowedCountries: allowedIso3,
			isExistingProfile: store.quoteResponse?.isExistingProfile || false,
		});

		// Update store
		rampsActions.setCalculatorData({
			country: resolution.value,
			state: store.state,
			amount: inputAmount || "0.00",
			currency: store.assetCode || "USDC",
		});

		// Log warning if any
		if (resolution.warning) {
			// Country resolution completed with warning
		}
	}, [
		//search.payout_country, // Only run if not already resolved
		serviceOptionsQuery.data,
		serviceOptionsQuery.isLoading,
		allCountries, // Full array needed (used in toIso3 call)
		allowedIso3, // Memoized list of allowed country codes
		countriesLoading,
		inputAmount, // Used in setCalculatorData
		// Only depend on specific search params that matter for country resolution
		search.country,
		search.payout_country,
		search.country_src,
		store.assetCode,
		// Only depend on specific store values that matter
		store.payoutCountry,
		store.quoteResponse?.senderProfile?.address?.countryCode,
		store.quoteResponse?.isExistingProfile,
		store.suggestedCountry,
		store.state,
	]);

	// Region pre-selection from serviceOptions response
	useEffect(() => {
		const hasServiceOptions =
			!!serviceOptionsQuery.data && !serviceOptionsQuery.isLoading;

		if (!hasServiceOptions) return;

		// Skip if user has already manually selected a subdivision
		if (hasUserSelectedSubdivision.current) return;

		const { region, withdrawCountry } = serviceOptionsQuery.data;

		// Check if region is present and not empty
		if (!region || region.trim() === "") return;

		// Only pre-select for supported countries, and only if the country is
		// actually in the cash-out allowed list (RAMPS-4406)
		if (!withdrawCountry || !["USA", "CAN", "MEX"].includes(withdrawCountry))
			return;
		if (!allowedIso3.includes(withdrawCountry)) return;

		// Find the corresponding subdivision value
		const subdivisionValue = findSubdivisionByRegionName(
			region,
			withdrawCountry,
		);

		// Check if subdivision is valid and not in unsupported states list
		if (subdivisionValue && !notSupportedUSStates.includes(subdivisionValue)) {
			rampsActions.setSubdivision(subdivisionValue, withdrawCountry);
		}
	}, [
		serviceOptionsQuery.data,
		serviceOptionsQuery.isLoading,
		notSupportedUSStates,
		allowedIso3,
	]);

	// Get effective country for display (payout_country with fallback to legacy country, then suggested country)
	const selectedCountryIso3 = toIso3(
		store.payoutCountry || store.suggestedCountry,
		allCountries,
	);

	// Country options for the <Select/>
	const countryOptions: CountryOption[] = filteredCountriesForOptions.map(
		(country: Country) => ({
			value: country.iso3Code, // value stored in URL
			label: country.name,
			countryCode: country.iso2Code.toLowerCase(), // for CircleFlag
		}),
	);

	const selectedCountry = countryOptions.find(
		(c) => c.value === selectedCountryIso3,
	);

	// Determine if selected country needs state/region selection
	const countryNeedsRegions = ["USA", "MEX", "CAN"].includes(
		selectedCountryIso3,
	);

	// Automatically open subdivision sheet when switching to subdivision mode
	// This ensures the sheet opens when mode changes to subdivision
	useEffect(() => {
		if (locationSheetMode === "subdivision" && !showLocationSheet) {
			setShowLocationSheet(true);
			// Reset the switching flag after a brief delay
			setTimeout(() => {
				setSwitchingToSubdivision(false);
			}, 100);
		}
	}, [locationSheetMode, showLocationSheet]);

	const iso3CodeForRegions = selectedCountryIso3;

	const regionsQuery = useQuery({
		queryKey: ["regions", iso3CodeForRegions],
		queryFn: async () => {
			if (!iso3CodeForRegions) {
				throw new Error(`Cannot determine iso3Code for regions`);
			}
			return apiClient.referenceData.getRegions(iso3CodeForRegions);
		},
		enabled: !!authToken && !!countryNeedsRegions && !!iso3CodeForRegions,
		staleTime: 5 * 60 * 1000,
	});

	// RAMPS-4405: any core API failure (after retries) leaves the flow
	// unrecoverable — collect support codes and swap in the error splash below.
	const coreApiErrorCodes = useMemo(() => {
		const codes: CoreApiErrorCode[] = [];
		if (store.isPrepareFailed) codes.push(CORE_API_ERROR_CODES.prepare);
		if (isCountriesError) codes.push(CORE_API_ERROR_CODES.countries);
		if (regionsQuery.isError) codes.push(CORE_API_ERROR_CODES.regions);
		if (serviceOptionsQuery.isError)
			codes.push(CORE_API_ERROR_CODES.serviceOptions);
		return codes;
	}, [
		store.isPrepareFailed,
		isCountriesError,
		regionsQuery.isError,
		serviceOptionsQuery.isError,
	]);

	// Region options
	const regionOptions: RegionOption[] = (regionsQuery.data || [])
		.filter((region: Region) => {
			// Filter out unsupported US states when country is USA
			if (selectedCountryIso3 === "USA") {
				return !notSupportedUSStates.includes(region.code);
			}
			return true;
		})
		.map((region: Region) => ({ value: region.code, label: region.name }));

	// Find the service option that matches the user's currently selected
	// payoutOptionCode (e.g. WILL_CALL, BANK_DEPOSIT). Falls back to the first
	// option if no match — preserves prior behavior on unknown service options.
	const getActiveServiceOption = () => {
		const opts = serviceOptionsQuery.data?.serviceOptions;
		if (!opts || opts.length === 0) return undefined;
		return (
			opts.find((o) => o.payoutOptionCode === store.serviceOption) || opts[0]
		);
	};

	// Currencies the user is allowed to receive in for the chosen serviceOption.
	const getAvailablePayoutCurrencies = (): string[] => {
		return getActiveServiceOption()?.payoutCurrencyCodes ?? [];
	};

	// Default payout currency when the store value is empty or stale.
	// Rule: localCurrencyCode if supported, else USD if supported, else first.
	const getDefaultPayoutCurrency = (): string => {
		const available = getAvailablePayoutCurrencies();
		if (available.length === 0) return "USD";
		const local = getActiveServiceOption()?.localCurrencyCode;
		if (local && available.includes(local)) return local;
		if (available.includes("USD")) return "USD";
		return available[0];
	};

	// Receivable currency: prefer the user's pick (if still valid for the
	// current country/option), otherwise apply the default rule.
	const getReceiveCurrency = (): string => {
		const available = getAvailablePayoutCurrencies();
		if (store.payoutCurrency && available.includes(store.payoutCurrency)) {
			return store.payoutCurrency;
		}
		return getDefaultPayoutCurrency();
	};

	// Helper to extract display data from QuoteResponse
	const getQuoteDisplayData = (quoteResponse: QuoteResponse | undefined) => {
		if (!quoteResponse?.transactions[0]) return null;
		const transaction = quoteResponse.transactions[0];
		const sendAmountDetails = transaction.sendAmountDetails;
		const payoutAmountDetails = transaction.payoutAmountDetails;

		return {
			exchangeRate: Number(payoutAmountDetails?.fxRate || 1),
			fees: {
				value: Number(sendAmountDetails?.fees?.value || 0),
				currencyCode: String(sendAmountDetails?.fees?.currencyCode || "USDC"),
			},
			partnerFees: {
				value: Number(sendAmountDetails?.partnerFees?.value || 0),
				currencyCode: String(
					sendAmountDetails?.partnerFees?.currencyCode || "USDC",
				),
			},
			taxes: {
				value: Number(payoutAmountDetails?.taxes?.value || 0),
				currencyCode: String(payoutAmountDetails?.taxes?.currencyCode || "USD"),
			},
			totalSendAmount: {
				value: Number(sendAmountDetails?.totalAmount?.value || 0),
				currencyCode: String(
					sendAmountDetails?.totalAmount?.currencyCode || "USDC",
				),
			},
			totalReceiveAmount: {
				value: Number(payoutAmountDetails?.totalAmount?.value || 0),
				currencyCode: String(
					payoutAmountDetails?.totalAmount?.currencyCode || "USD",
				),
			},
		};
	};

	// Helper to extract estimated delivery from QuoteResponse
	const getEstimatedDelivery = (
		quoteResponse: QuoteResponse | undefined,
	): string | null => {
		if (!quoteResponse?.transactions) return null;

		// Find transaction matching the selected service option
		// BANK_DEPOSIT uses "All Banks"/"Other Banks"
		// DIRECT_TO_ACCT uses "All Wallets"
		const matchingTransaction = quoteResponse.transactions.find(
			(transaction) =>
				transaction.serviceOptionCode === store.serviceOption &&
				(transaction.serviceOptionRoutingName === "All Banks" ||
					transaction.serviceOptionRoutingName === "Other Banks" ||
					transaction.serviceOptionRoutingName === "All Wallets"),
		);

		return matchingTransaction?.estimatedDelivery || null;
	};

	// Get transaction quote for exchange rate
	const quoteQuery = useQuery({
		queryKey: [
			"quote",
			store.operation,
			store.payoutCountry,
			store.state,
			store.assetCode,
			// Refetch when the user picks a different payout currency for a
			// multi-currency country so the BFF returns FX in the chosen target.
			store.payoutCurrency,
			// Refetch when active locale changes so `userLanguage` on the quote
			// payload matches the UI Lingui has activated. Without this the
			// initial quote fires before /prepare populates store.lang and stays
			// stale with the default tag.
			store.lang,
		],
		queryFn: async () => {
			const searchParams: SearchParams = {
				...search,
				amount: formatAmount(inputAmount),
			} as SearchParams;
			const quoteRequest = buildQuoteRequest(
				searchParams,
				getReceiveCurrency(),
				inputAmount, // Todo: review: looks like this param may not be used at all in buildQuoteRequest
				store.serviceOption,
				store.payoutCountry,
				store.state,
			);
			if (!quoteRequest) {
				throw new Error("Cannot build quote request - missing required data");
			}

			// Validate min/max amounts before making API call
			if (inputAmount && Number(inputAmount) < minAmount) {
				const errorMsg = t`Minimum amount is $${minAmount.toFixed(2)}`;
				toast.validationError(errorMsg);
				throw new Error(errorMsg);
			}

			if (inputAmount && Number(inputAmount) > maxAmount) {
				const errorMsg = t`Maximum amount is $${maxAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
				toast.validationError(errorMsg);
				throw new Error(errorMsg);
			}

			const response = await apiClient.quote.getQuote(quoteRequest);

			// Check for errors in the response and display them
			if (response.errors && response.errors.length > 0) {
				const error = response.errors[0];
				const errorMessage = getLocalizedErrorMessage(error);

				// Show toast notification for quote error
				toast.quoteError(errorMessage);
			}

			// Update sender profile if available
			if (response.senderProfile) {
				const sep9Address = response.senderProfile.address;

				// Process address lines to ensure they meet length requirements
				const processedAddress = processAddressLines(
					sep9Address?.line1,
					sep9Address?.line2,
				);

				// Normalize names: comma-split lastName and fit API length limits.
				// (RAMPS-4410 removed the duplicate-token dropping that used to
				// truncate legitimate repeated surnames.)
				const normalizedNames = normalizeSenderNames(response.senderProfile);

				// Update sender profile with processed address and names
				const updatedProfile = {
					...response.senderProfile,
					address: {
						...sep9Address,
						line1: processedAddress.line1,
						line2: processedAddress.line2,
					},
					...normalizedNames,
				};

				rampsActions.setSenderProfile(updatedProfile);
			}

			// Store the response even if there are errors (as requested)
			if (response) {
				rampsActions.setQuoteResponse(response);
				// Set serviceOptionRoutingCode for:
				// 1. WILL_CALL (cash pickup) - always set
				// 2. Bank services - ONLY set for returning users with saved accounts
				//    This ensures:
				//    - Returning users: auto-proceed with their saved bank account
				//    - New users: can choose from available bank options on digital-offramp-info page
				//    - Fallback: transactionPayloadBuilder will find routing code if needed
				if (store.serviceOption === "WILL_CALL") {
					// WILL_CALL always has single transaction
					const matchingTransaction = response.transactions.find(
						(tx) => tx.serviceOptionCode === store.serviceOption,
					);
					rampsActions.setServiceOptionRoutingCode(
						matchingTransaction?.serviceOptionRoutingCode || "",
					);
				} else if (
					isBankService(store.serviceOption) &&
					store.digitalProfileId &&
					store.selectedBankAccount &&
					store.operation !== "deposit"
				) {
					// For returning users with saved bank accounts, auto-set routing code
					// This allows them to skip the bank selection page
					const matchingTransaction = response.transactions.find(
						(tx) => tx.serviceOptionCode === store.serviceOption,
					);
					rampsActions.setServiceOptionRoutingCode(
						matchingTransaction?.serviceOptionRoutingCode || "",
					);
					// New users without saved accounts: don't set routing code
					// They will choose their bank on the digital-offramp-info page
				}
			}

			// Store the payout currency from the quote response
			const payoutCurrency =
				response.transactions[0]?.payoutAmountDetails?.totalAmount
					?.currencyCode;
			if (payoutCurrency) {
				rampsActions.setPayoutCurrency(payoutCurrency);
			}

			return response;
		},
		enabled: (() => {
			const effectiveCountry = store.payoutCountry;

			// Wait for service options to load and provide currency codes
			const hasPayoutCurrency =
				!!serviceOptionsQuery.data?.serviceOptions[0]?.payoutCurrencyCodes
					?.length;
			// Simplified amount validation for quotes
			const isValidAmountForQuote = () => {
				if (!inputAmount) return false;
				const amountStr = String(inputAmount);
				if (amountStr.trim() === "") return false;
				const numAmount = Number.parseFloat(amountStr);

				// Check against min/max amounts (from feature flags or defaults)
				if (numAmount < minAmount || numAmount > maxAmount) return false;

				// For editable amounts, use complete amount detection to reduce API calls
				if (isAmountEditable) {
					return isAmountReady(numAmount);
				}

				// For fixed amounts (URL), use existing validation
				return (
					!Number.isNaN(numAmount) && /^\d+(\.\d{1,4})?$/.test(amountStr.trim())
				);
			};

			return (
				!!authToken &&
				!!effectiveCountry &&
				hasPayoutCurrency &&
				isValidAmountForQuote() &&
				readyForQuote &&
				isFormValid(
					{ ...search },
					inputAmount,
					store.payoutCountry,
					store.state,
				)
			);
		})(),
		staleTime: 30000,
		retry: 1,
	});

	const selectedRegion = regionOptions.find((r) => r.value === store.state);

	// Auto-trigger quote for locked amounts (when amount comes from URL or v3 prepare)
	useEffect(() => {
		const hasLockedAmount =
			(store.apiVersion === "v3" && store.prepareAmount) ||
			(!isAmountEditable && search.amount);
		const amountToCheck =
			store.apiVersion === "v3" && store.prepareAmount
				? store.prepareAmount
				: search.amount || inputAmount;

		if (
			hasLockedAmount &&
			!!authToken &&
			!!store.payoutCountry &&
			Number(amountToCheck) >= 1 &&
			isFormValid(
				{ token: authToken }, // Only pass token property to avoid search object dependency
				amountToCheck,
				store.payoutCountry,
				store.state,
			) &&
			(!countryNeedsRegions || !!store.state) // If country needs regions, state must be set
		) {
			setReadyForQuote(true);
		}
	}, [
		store.prepareAmount,
		store.apiVersion,
		isAmountEditable,
		authToken,
		search.amount,
		store.payoutCountry,
		store.state,
		countryNeedsRegions,
		inputAmount,
	]);

	const handleAmountChange = async (value: string) => {
		// Reset quote readiness when user starts typing (to allow future quotes)
		if (readyForQuote) {
			setReadyForQuote(false);
		}
		setInputAmount(value);
		setIsPendingNewQuote(true);

		// Clear any existing timeout
		if (readyForQuoteTimeoutRef.current) {
			clearTimeout(readyForQuoteTimeoutRef.current);
		}

		const numValue = Number(value);
		if (!value || numValue < 1) return;

		// Set a new timeout to set readyForQuote after 1.5 second
		readyForQuoteTimeoutRef.current = setTimeout(() => {
			const formattedAmount = formatAmount(value);
			setInputAmount(formattedAmount);
			// Don't trigger quote if outside min/max range
			const error = minMaxError(formattedAmount, minAmount, maxAmount);
			if (error.isError) {
				toast.validationError(error.errorMsg || "Invalid amount");
				setIsPendingNewQuote(false);
				return;
			}

			// Track amount change event
			const analyticsProps: CalculatorAnalyticsProperties = {
				amount: Number(formattedAmount),
				source_currency: store.assetCode || "USDC",
				destination_currency: store.payoutCurrency || "USD",
				service_option: store.serviceOption,
			};
			trackEvent(ANALYTICS_EVENTS.AMOUNT_CHANGED, analyticsProps);

			setReadyForQuote(true);
			setIsPendingNewQuote(false);
			quoteQuery.refetch();
		}, 1500);
	};

	const handleAmountCommit = () => {
		if (readyForQuote) {
			setReadyForQuote(false);
		}
		setIsPendingNewQuote(true);

		// Clear any existing timeout
		if (readyForQuoteTimeoutRef.current) {
			clearTimeout(readyForQuoteTimeoutRef.current);
		}

		// Validate amount against minimum
		if (!inputAmount || Number(inputAmount) < minAmount) {
			const errorMsg = t`Minimum amount is $${minAmount.toFixed(2)}`;
			toast.validationError(errorMsg);
			setIsPendingNewQuote(false);
			return;
		}

		setInputAmount(formatAmount(inputAmount));
		const error = minMaxError(inputAmount, minAmount, maxAmount);
		if (error.isError) {
			toast.validationError(error.errorMsg || "Invalid amount");
			setIsPendingNewQuote(false);
			return;
		}

		setReadyForQuote(true);
		setIsPendingNewQuote(false);
		quoteQuery.refetch();
	};

	// Check if payout country is locked
	const isCountryLocked = (() => {
		const locks = search.locks?.split(",") || [];
		return locks.includes("payout_country");
	})();

	// Event handlers - maintain URL and store sync
	const handleCountryChange = (newIso3: string) => {
		// Don't allow changes if field is locked
		if (isCountryLocked) {
			return;
		}

		// Reset quote state to trigger new quote with new country/currency
		setReadyForQuote(false);

		// Clear payout currency so it will be refetched for new country
		rampsActions.setPayoutCurrency("");
		rampsActions.setPayoutCountry(newIso3);
		rampsActions.setCalculatorData({
			country: newIso3,
			state: "",
			amount: inputAmount || "0.00",
			currency: store.assetCode || "USDC",
		});

		// Re-enable quote after state update
		// This will trigger once serviceOptions loads with new currency
		setTimeout(() => {
			if (inputAmount && Number(inputAmount) >= 1) {
				setReadyForQuote(true); //TODO: Review this could be removed in the Future
			}
		}, 100);
	};

	const handleStateChange = (newState: string) => {
		hasUserSelectedSubdivision.current = true;
		rampsActions.setCalculatorData({
			country: selectedCountryIso3,
			state: newState,
			amount: inputAmount || "0.00",
			currency: store.assetCode || "USDC",
		});
	};

	// Track if we're switching to subdivision mode (to prevent sheet from closing)
	const [switchingToSubdivision, setSwitchingToSubdivision] = useState(false);

	// Handler for bottom sheet location selection (country + subdivision)
	const handleLocationSelect = (value: string) => {
		if (locationSheetMode === "country") {
			// Don't allow changes if country is locked
			if (isCountryLocked) {
				return;
			}

			// Country was selected
			handleCountryChange(value);

			// Check if country has subdivisions
			const countriesWithSubdivisions = getCountriesWithSubdivisions();
			if (countriesWithSubdivisions.includes(value)) {
				// Switch to subdivision selection mode and keep sheet open
				setSwitchingToSubdivision(true);
				setLocationSheetMode("subdivision");
				// Sheet will stay open (useEffect will handle it, and onOpenChange will prevent closing)
			} else {
				// No subdivisions needed, close sheet and reset to country mode
				setSwitchingToSubdivision(false);
				setShowLocationSheet(false);
				setLocationSheetMode("country");
			}
		} else {
			// Subdivision was selected
			handleStateChange(value);
			setSwitchingToSubdivision(false);
			setShowLocationSheet(false);
			setLocationSheetMode("country");
		}
	};

	// RAMPS-4410: The SEP-9 name fields arrive inside senderProfile whether the
	// user is new or returning, so senderProfile's presence alone can't tell them
	// apart. A returning user with a saved MoneyGram profile carries a
	// primaryIdentification; a brand-new user does not. Only brand-new users are
	// asked to verify the (possibly partner-supplied) name before it is
	// permanently saved at sendValidate.
	const senderProfile = store.quoteResponse?.senderProfile;
	const isBrandNewUser =
		!!senderProfile && !senderProfile.primaryIdentification?.id;

	// Full legal name assembled from the store for the confirmation sheet.
	const getConfirmationName = () =>
		[
			capitalizeFirstLetter(store.firstName),
			capitalizeFirstLetter(store.middleName),
			capitalizeFirstLetter(store.lastName),
			capitalizeFirstLetter(store.secondLastName),
		]
			.filter(Boolean)
			.join(" ");

	// Advance through the normal cash-out flow (phone → … → review).
	const proceedToNextPage = () => {
		const searchWithoutFromReview = { ...search };
		delete searchWithoutFromReview.from_review;
		handleContinue(searchWithoutFromReview);
	};

	// User confirmed their name is correct: continue the flow.
	const handleConfirmName = () => {
		setShowNameConfirmDrawer(false);
		proceedToNextPage();
	};

	// User needs to fix their name: send them to the name page to edit it.
	const handleEditName = () => {
		setShowNameConfirmDrawer(false);
		navigate(
			navigateWithRampsState(search, {
				to: "/ramps/name",
				search: { step: "name" },
			}),
		);
	};

	const handleSubmit = () => {
		// Validate amount first
		const amountValidation = validateAmount(inputAmount);
		if (!amountValidation.isValid) {
			const errorMsg = amountValidation.error || t`Invalid amount`;
			toast.validationError(errorMsg);
			return;
		}

		// Check form validity (country, state, etc.)
		if (isFormValid(search, inputAmount, store.payoutCountry, store.state)) {
			// Track Continue button click for A/B experiment funnel
			// Only track if variant is loaded to ensure proper experiment attribution
			// (null variant would break funnel analysis in PostHog)
			if (experimentVariant !== null) {
				trackEvent(ANALYTICS_EVENTS.CALCULATOR_CONTINUE_CLICKED, {
					...getExperimentProperties(),
				});
			}

			// Track flow_started for funnel analysis (always fires, regardless of experiment)
			trackEvent("flow_started", {
				flow_type: "withdraw",
				amount: inputAmount ? Number(inputAmount) : undefined,
				country: store.payoutCountry,
			} as Record<string, unknown>);

			// Capture finalized amount with full currency context for analytics
			if (inputAmount && Number(inputAmount) > 0) {
				trackTransactionAmount({
					amount: Number(inputAmount),
					sourceCurrency: store.assetCode || "USDC",
					destinationCurrency: store.payoutCurrency || "USD",
					serviceOption: store.serviceOption,
				});
			}

			// Sync local inputAmount to store if it changed
			// Country/state are already synced via handleCountryChange/handleStateChange
			const newAmount = inputAmount || "0.00";
			if (newAmount !== store.amount) {
				rampsActions.setCalculatorData({
					country: store.payoutCountry,
					state: store.state,
					amount: newAmount,
					currency: store.assetCode || "USDC",
				});
			}

			// RAMPS-4410: before advancing a brand-new user toward sendValidate,
			// have them verify the (possibly partner-prefilled) name is correct.
			// Returning users skip this — their name is already locked to their
			// MoneyGram profile. Skip on the review-edit path (already validated).
			if (isBrandNewUser && search.from_review !== "true") {
				setShowNameConfirmDrawer(true);
				return;
			}

			// Clear from_review flag when continuing to next step
			proceedToNextPage();
		}
	};

	// Get display text for current location selection
	const getLocationDisplayText = (): string => {
		if (countriesLoading) {
			return t`Loading...`;
		}
		// For countries with states, show just the state name when selected
		if (countryNeedsRegions && store.state && selectedRegion) {
			return selectedRegion.label;
		}
		// If country needs regions but none selected, prompt user to select
		if (countryNeedsRegions && !store.state) {
			return selectedCountryIso3 === "USA"
				? t`Select a state`
				: t`Select a region`;
		}
		return selectedCountry?.label || t`Select location`;
	};

	if (isCashOutMaintenance) {
		return (
			<MaintenancePage
				flow="cash-out"
				maintenance={serviceOptionsQuery.data?.maintenance}
			/>
		);
	}

	if (coreApiErrorCodes.length > 0) {
		return (
			<CoreApiErrorPage
				flow="cash-out"
				errorCodes={coreApiErrorCodes}
				transactionId={store.transactionId}
			/>
		);
	}

	return (
		<div className="flex min-h-dvh flex-col">
			<div className="bg-ramps-background-base px-4">
				<div className="flex items-center justify-center py-6">
					{/* Country/Location selector */}
					<div className="mx-4">
						{countriesLoading ? (
							<div className="flex items-center justify-center gap-2 rounded-lg bg-ramps-background-surface-2 px-4 py-2">
								<div className="h-5 w-5 animate-pulse rounded-full bg-gray-300" />
								<RampsText
									variant="body-default"
									className="font-medium"
									color="primary"
								>
									{t`Loading...`}
								</RampsText>
							</div>
						) : (
							<>
								{/* Country Selection */}
								{locationSheetMode === "country" && (
									<RampsSelectBottomSheet
										key="country-select"
										label=""
										sheetTitle={t`Select country`}
										options={countryOptions.map((c) => ({
											value: c.value,
											label: c.label,
											countryCode: c.countryCode,
										}))}
										value={selectedCountryIso3}
										onSelect={handleLocationSelect}
										size="full"
										searchPlaceholder={t`Search countries...`}
										renderOptionIcon={(option) =>
											option.countryCode ? (
												<CircleFlag
													countryCode={option.countryCode}
													className="h-6 w-6"
												/>
											) : null
										}
										renderTriggerContent={() => (
											<div className="flex items-center gap-2">
												<CircleFlag
													countryCode={selectedCountry?.countryCode || "us"}
													className="h-5 w-5"
												/>
												<RampsText
													variant="body-default"
													className="font-medium"
													color="primary"
												>
													{getLocationDisplayText()}
												</RampsText>
											</div>
										)}
										triggerClassName="bg-ramps-background-surface-2 rounded-lg px-4 py-2 w-auto"
										placeholder={t`Select location`}
										disabled={isCountryLocked || search.from_review === "true"}
										open={showLocationSheet}
										onOpenChange={(open) => {
											if (open) {
												// When opening, check if we should show subdivision mode instead
												const countriesWithSubdivisions =
													getCountriesWithSubdivisions();
												if (
													selectedCountryIso3 &&
													countriesWithSubdivisions.includes(
														selectedCountryIso3,
													)
												) {
													// Country needs a subdivision, open subdivision mode
													// whether or not one has been selected yet
													setLocationSheetMode("subdivision");
												} else {
													// Open country mode
													setLocationSheetMode("country");
												}
												setShowLocationSheet(true);
											} else {
												// Prevent closing if we're switching to subdivision mode
												if (switchingToSubdivision) {
													return;
												}
												setShowLocationSheet(false);
												setLocationSheetMode("country");
												setSwitchingToSubdivision(false);
											}
										}}
									/>
								)}

								{/* Subdivision Selection */}
								{locationSheetMode === "subdivision" && (
									<RampsSelectBottomSheet
										key="subdivision-select"
										label=""
										sheetTitle={
											selectedCountryIso3 === "USA"
												? t`Select state`
												: t`Select region`
										}
										options={regionOptions.map((r) => ({
											value: r.value,
											label: r.label,
										}))}
										value={store.state}
										onSelect={handleLocationSelect}
										size="full"
										searchPlaceholder={t`Search...`}
										renderTriggerContent={() => (
											<div className="flex items-center gap-2">
												<CircleFlag
													countryCode={selectedCountry?.countryCode || "us"}
													className="h-5 w-5"
												/>
												<RampsText
													variant="body-default"
													className="font-medium"
													color="primary"
												>
													{getLocationDisplayText()}
												</RampsText>
											</div>
										)}
										triggerClassName="bg-ramps-background-surface-2 rounded-lg px-4 py-2 w-auto"
										placeholder={
											selectedCountryIso3 === "USA"
												? t`Select a state`
												: t`Select a region`
										}
										disabledValues={
											selectedCountryIso3 === "USA" ? notSupportedUSStates : []
										}
										onBack={() => {
											setLocationSheetMode("country");
											// Keep sheet open when going back to country
										}}
										backLabel={t`Countries`}
										open={showLocationSheet}
										onOpenChange={(open) => {
											setShowLocationSheet(open);
											if (!open) {
												setLocationSheetMode("country");
											}
										}}
									/>
								)}
							</>
						)}
					</div>
				</div>

				{/* Progress bar */}
				<div className="mb-8">
					<Progress
						value={10}
						className="h-1 bg-ramps-background-surface-2 *:bg-ramps-text-primary"
						data-testid="calculator-progress"
					/>
				</div>

				{/* Cash-out amount section - A/B test with feature flag */}
				{isNewCalculatorEnabled ? (
					<div className="mb-16">
						<Calculator
							inputAmount={inputAmount}
							isAmountEditable={isAmountEditable}
							onAmountChange={handleAmountChange}
							onAmountCommit={handleAmountCommit}
							assetCode={store.assetCode || "USDC"}
							receiveCurrency={getReceiveCurrency()}
							quoteData={getQuoteDisplayData(quoteQuery.data)}
							isLoading={quoteQuery.isLoading}
							availablePayoutCurrencies={getAvailablePayoutCurrencies()}
							onPayoutCurrencyChange={(value) =>
								rampsActions.setPayoutCurrency(value)
							}
						/>
						{store.serviceOption === "WILL_CALL" && (
							<div className="mt-8 flex items-center justify-center">
								<NearbyLocationsCounter
									countryCode={
										selectedCountry?.countryCode?.toUpperCase() || "US"
									}
									onOpenLocator={() => setShowLocatorSheet(true)}
								/>
							</div>
						)}
					</div>
				) : (
					<div className="mb-16 text-center">
						<RampsText variant="body-bold" color="secondary" className="mb-2">
							{t`Withdraw amount`}
						</RampsText>

						<div className="mb-6 flex items-center justify-center gap-3">
							<EditableText
								value={inputAmount}
								isEditable={isAmountEditable}
								onChange={handleAmountChange}
								onCommit={handleAmountCommit}
								className="font-normal text-6xl"
								variant="number-md"
								color="primary"
								placeholder="0.00"
								caretWidthPx={4}
								blinkMs={900}
								autoFocus={isAmountEditable}
							/>
							<div className="flex flex-col items-center gap-0">
								<TokenUSDC className="h-6 w-6" />
								<RampsText variant="body-bold" color="disabled">
									{store.assetCode}
								</RampsText>
							</div>
						</div>

						{/* RAMPS-4335: payout currency selector — only rendered when the
							selected country's chosen serviceOption supports >1 currency. */}
						{(() => {
							const available = getAvailablePayoutCurrencies();
							if (available.length <= 1) return null;
							const current = getReceiveCurrency();
							return (
								<div className="mb-6 flex items-center justify-center gap-2">
									<RampsText variant="body-default" color="secondary">
										{t`Receive in`}
									</RampsText>
									<RampsSelectBottomSheet
										key={`payout-currency-legacy-${store.payoutCountry}-${store.serviceOption}`}
										label=""
										sheetTitle={t`Select payout currency`}
										options={available.map((code) => ({
											value: code,
											label: code,
										}))}
										value={current}
										onSelect={(value) => rampsActions.setPayoutCurrency(value)}
										size="compact"
										showSearch={false}
										renderTriggerContent={() => (
											<RampsText variant="body-bold" color="primary">
												{current}
											</RampsText>
										)}
										triggerClassName="w-auto px-3 py-1 rounded-md bg-ramps-background-surface-2"
										placeholder={current}
									/>
								</div>
							);
						})()}

						{/* You get section */}
						{/* Exchange currency display section - COMMENTED OUT */}
						{/* eslint-disable-next-line @typescript-eslint/no-unused-expressions */}
						{false && (
							<div className="mb-6">
								<RampsText
									variant="body-bold"
									color="secondary"
									className="mb-2"
								>
									{t`Exchange currency`}
								</RampsText>

								<div className="flex items-center justify-center gap-3">
									<RampsText
										variant="number-md"
										color="primary"
										className="font-normal text-6xl"
									>
										{(() => {
											const displayData = getQuoteDisplayData(quoteQuery.data);
											const exchangeRate = displayData?.exchangeRate ?? 1;
											if (displayData && exchangeRate !== null) {
												// Show total converted amount before fees (USDC amount × exchange rate)
												const usdcAmount = Number.parseFloat(
													inputAmount || "0",
												);
												const convertedAmount = usdcAmount * exchangeRate;
												return formatNumber(convertedAmount);
											}
											// Fallback when no quote data
											return formatNumber(Number.parseFloat(inputAmount) || 0);
										})()}
									</RampsText>
									<div className="flex flex-col items-center gap-0.5">
										<CircleFlag
											countryCode={selectedCountry?.countryCode || "us"}
											className="h-4 w-4"
										/>
										<RampsText variant="body-bold" color="disabled">
											{getReceiveCurrency()}
										</RampsText>
									</div>
								</div>
							</div>
						)}

						{/* Exchange rate one-liner - KEPT VISIBLE */}
						{(() => {
							const d = getQuoteDisplayData(quoteQuery.data);
							if (d && inputAmount) {
								return (
									<RampsText variant="body-default" color="primary">
										{`${t`Exchange rate:`} 1 ${store.assetCode || "USDC"} = ${formatNumber(d.exchangeRate, { minimumFractionDigits: 4, maximumFractionDigits: 4 })} ${getReceiveCurrency()}`}
									</RampsText>
								);
							}
							if (quoteQuery.isLoading) {
								return (
									<RampsText variant="body-default" color="primary">
										{t`Loading exchange rate...`}
									</RampsText>
								);
							}
							// Empty spacer div to maintain layout spacing
							return <div className="h-6" />;
						})()}

						{/* Spacer to maintain layout */}
						<div className="h-6" />
						{store.serviceOption === "WILL_CALL" && (
							<div className="mt-8 flex items-center justify-center">
								<NearbyLocationsCounter
									countryCode={
										selectedCountry?.countryCode?.toUpperCase() || "US"
									}
									onOpenLocator={() => setShowLocatorSheet(true)}
								/>
							</div>
						)}
					</div>
				)}
			</div>
			{/* Fees section */}
			<div className="mt-auto rounded-t-2xl bg-gradient-ramps-status-bl p-6">
				<div className="mb-3 flex items-center justify-between">
					<RampsText variant="body-default" color="primary">
						{t`Fees`}
					</RampsText>
					{quoteQuery.isLoading ? (
						<Skeleton className="h-5 w-20 border border-gray-300/20 bg-gray-200/30" />
					) : (
						<RampsText variant="body-default" color="primary">
							{(() => {
								const displayData = getQuoteDisplayData(quoteQuery.data);
								if (displayData && inputAmount) {
									// Display fee as deducted amount in local currency
									const feeInUSDC = displayData?.partnerFees?.value || 0;
									const exchangeRate = displayData.exchangeRate;
									const feeInLocalCurrency = feeInUSDC * exchangeRate;
									const localCurrency =
										displayData.totalReceiveAmount.currencyCode;
									return `-${formatNumber(feeInLocalCurrency)} ${localCurrency}`;
								}
								return `-${formatNumber(0)} ${getReceiveCurrency()}`;
							})()}
						</RampsText>
					)}
				</div>
				{/* Taxes - Only show when taxes value > 0 */}
				{(() => {
					const displayData = getQuoteDisplayData(quoteQuery.data);
					const taxesValue = displayData?.taxes?.value || 0;
					if (taxesValue > 0 && inputAmount && displayData) {
						return (
							<div className="mb-3 flex items-center justify-between">
								<RampsText variant="body-default" color="primary">
									{t`Taxes`}
								</RampsText>
								{quoteQuery.isLoading ? (
									<Skeleton className="h-5 w-20 border border-gray-300/20 bg-gray-200/30" />
								) : (
									<RampsText variant="body-default" color="primary">
										{`-${formatNumber(taxesValue)} ${displayData.taxes.currencyCode}`}
									</RampsText>
								)}
							</div>
						);
					}
					return null;
				})()}
				{/* Available - Bank services only */}
				{isBankService(store.serviceOption) &&
					(() => {
						const estimatedDelivery = getEstimatedDelivery(quoteQuery.data);
						if (estimatedDelivery && inputAmount) {
							return (
								<div className="mb-3 flex items-center justify-between">
									<RampsText variant="body-default" color="primary">
										{t`Available`}
									</RampsText>
									<RampsText variant="body-default" color="primary">
										{estimatedDelivery}
									</RampsText>
								</div>
							);
						}
						return null;
					})()}

				{/* You Get row - only shown when old calculator UI is active */}
				{!isNewCalculatorEnabled && (
					<div className="mb-6 flex items-center justify-between">
						<RampsText variant="body-bold" color="primary">
							{t`You Get`}
						</RampsText>
						{quoteQuery.isLoading ? (
							<Skeleton className="h-6 w-24 border border-gray-300/20 bg-gray-200/30" />
						) : (
							<RampsText variant="body-bold" color="primary">
								{(() => {
									const displayData = getQuoteDisplayData(quoteQuery.data);
									if (displayData && inputAmount) {
										// Show the amount customer receives in local currency (directly from API)
										return `${formatNumber(displayData.totalReceiveAmount.value)} ${displayData.totalReceiveAmount.currencyCode}`;
									}
									// Fallback when no quote data
									const val = parseFloat(inputAmount || "0");
									return `${formatNumber(val)} ${getReceiveCurrency()}`;
								})()}
							</RampsText>
						)}
					</div>
				)}
				{/* Continue Button */}
				<RampsButton
					onClick={handleSubmit}
					disabled={
						!store.payoutCountry ||
						(countryNeedsRegions && !store.state) ||
						!inputAmount ||
						isPendingNewQuote ||
						quoteQuery.isFetching ||
						!quoteQuery.data ||
						quoteQuery.isError ||
						(quoteQuery.data?.errors && quoteQuery.data.errors.length > 0) ||
						minMaxError(inputAmount, minAmount, maxAmount).isError
					}
					loading={quoteQuery.isFetching}
					className="h-12 w-full rounded-lg bg-black font-medium text-white hover:bg-gray-800"
				>
					{t`Continue`}
				</RampsButton>
			</div>

			<Drawer open={showLocatorSheet} onOpenChange={setShowLocatorSheet}>
				<DrawerContent className="flex h-[85vh] max-h-[90vh] flex-col">
					<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
						<Locator
							readOnly
							defaultView="list"
							onBack={() => setShowLocatorSheet(false)}
						/>
					</div>
				</DrawerContent>
			</Drawer>

			{/* Legal name verification for brand-new users (RAMPS-4410) */}
			<LegalNameConfirmationDrawer
				open={showNameConfirmDrawer}
				onOpenChange={setShowNameConfirmDrawer}
				name={getConfirmationName()}
				onConfirm={handleConfirmName}
				onEdit={handleEditName}
			/>
		</div>
	);
}

export const Route = createFileRoute("/ramps/calculator")({
	validateSearch: (search) => validateRampsSearch(search),
	component: CalculatorPage,
});
